/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import TaskCard from './TaskCard';
import { Clock, Layers, User, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import SelectedTeamsBar from './SelectedTeamsBar';
import { Task, ExternalTask } from '../types';

interface ScrollerProps {
    children: React.ReactNode;
}

const HorizontalScroller: React.FC<ScrollerProps> = ({ children }) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);
    const scrollInterval = React.useRef<any>(null);

    const checkScroll = () => {
        if (!scrollRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setShowLeft(scrollLeft > 10);
        setShowRight(scrollLeft < scrollWidth - clientWidth - 10);
    };

    React.useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [children]);

    const startContinuousScroll = (direction: 'left' | 'right') => {
        stopContinuousScroll(); // Safety clear
        scroll(direction, false); // Single jump first
        scrollInterval.current = setInterval(() => scroll(direction, true), 16);
    };

    const stopContinuousScroll = () => {
        if (scrollInterval.current) {
            clearInterval(scrollInterval.current);
            scrollInterval.current = null;
        }
    };

    React.useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        window.addEventListener('mouseup', stopContinuousScroll);
        return () => {
            window.removeEventListener('resize', checkScroll);
            window.removeEventListener('mouseup', stopContinuousScroll);
            stopContinuousScroll();
        };
    }, [children]);

    const scroll = (direction: 'left' | 'right', isContinuous = false) => {
        if (!scrollRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        
        // Stop if we hit boundaries while continuous
        if (isContinuous) {
            if (direction === 'left' && scrollLeft <= 0) return stopContinuousScroll();
            if (direction === 'right' && scrollLeft >= scrollWidth - clientWidth) return stopContinuousScroll();
        }

        const amount = isContinuous ? (direction === 'left' ? -15 : 15) : (direction === 'left' ? -600 : 600);
        
        if (isContinuous) {
            scrollRef.current.scrollBy({ left: amount, behavior: 'auto' });
        } else {
            scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
        }
    };

    return (
        <div className="relative group/scroller w-full min-w-0 h-full flex items-center">
            {/* Left Overlay/Arrow */}
            {showLeft && (
                <div 
                    onMouseDown={() => startContinuousScroll('left')}
                    className="absolute left-0 inset-y-0 z-20 w-12 bg-gradient-to-r from-white dark:from-gray-800 via-white/80 dark:via-gray-800/80 to-transparent flex items-center justify-start opacity-40 group-hover/scroller:opacity-100 transition-opacity duration-300 cursor-pointer"
                >
                    <div className="p-2 ml-1 text-gray-400 hover:text-brand-500 hover:scale-125 transition-all">
                        <ChevronLeft size={32} strokeWidth={2.5} />
                    </div>
                </div>
            )}

            <div 
                ref={scrollRef}
                onScroll={checkScroll}
                className="flex gap-4 overflow-x-auto w-full pb-3 pt-1 items-start scrollbar-hide"
            >
                {children}
            </div>

            {/* Right Overlay/Arrow */}
            {showRight && (
                <div 
                    onMouseDown={() => startContinuousScroll('right')}
                    className="absolute right-0 inset-y-0 z-20 w-12 bg-gradient-to-l from-white dark:from-gray-800 via-white/80 dark:via-gray-800/80 to-transparent flex items-center justify-end opacity-40 group-hover/scroller:opacity-100 transition-opacity duration-300 cursor-pointer"
                >
                    <div className="p-2 mr-1 text-gray-400 hover:text-brand-500 hover:scale-125 transition-all">
                        <ChevronRight size={32} strokeWidth={2.5} />
                    </div>
                </div>
            )}
        </div>
    );
};

const ManagerView: React.FC = () => {
    const { tasks, allUsers, getMyManagerTeamIds, activeProjectMap, user, openMemberDetail } = useStandUp();
    const [searchQuery, setSearchQuery] = useState('');
    const [visibleCount, setVisibleCount] = useState(4); // Incremental render for instant initial paint
    const renderStartTime = useRef(performance.now());

    if (!user) return null;

    // 1. Identify which teams I manage among the active ones
    const managerTeamIds = useMemo(() => getMyManagerTeamIds(), [getMyManagerTeamIds]);

    // 2. Filter tasks (Native by teamId, External by projectKey)
    const visibleTasks = useMemo(() => {
        const activeProjectKeys = Object.keys(activeProjectMap);
        return tasks.filter(t => {
            if (!t) return false;
            const isExternal = 'externalSource' in t;
            const extTask = t as ExternalTask;
            const nativeTask = t as Task;

            const isFromManagedTeam = isExternal 
                ? activeProjectKeys.includes(extTask.projectKey)
                : managerTeamIds.includes(nativeTask.teamId);

            const isAssignedToMe = t.assigneeId === user.uid;
            return isFromManagedTeam || isAssignedToMe;
        });
    }, [tasks, activeProjectMap, managerTeamIds, user.uid]);

    // 3. Filter users (Preserving existing logic)
    const visibleUsers = useMemo(() => {
        return allUsers.filter(u => {
            const isInMyManagedTeam = u.teamIds?.some(tid => managerTeamIds.includes(tid));
            const isMe = u.id === user.uid;
            return isInMyManagedTeam || isMe;
        });
    }, [allUsers, managerTeamIds, user.uid]);

    // Sort users alphabetically
    const sortedUsers = useMemo(() => {
        return [...visibleUsers].sort((a, b) => {
            const nameA = a.displayName || '';
            const nameB = b.displayName || '';
            return nameA.localeCompare(nameB);
        });
    }, [visibleUsers]);

    // Group filtered tasks by assignee
    const tasksByUser = useMemo(() => {
        const grouped: Record<string, typeof tasks> = {};
        visibleTasks.forEach(t => {
            if (!t) return;
            const key = t.assigneeId || 'unassigned';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(t);
        });
        return grouped;
    }, [visibleTasks]);

    // Load the rest of the users after the initial paint
    useEffect(() => {
        if (visibleCount < sortedUsers.length) {
            const timer = setTimeout(() => {
                setVisibleCount(sortedUsers.length);
            }, 50); // 50ms gap is enough for a smooth paint
            return () => clearTimeout(timer);
        }
    }, [visibleCount, sortedUsers.length]);

    // Additive Logging
    useEffect(() => {
        const duration = performance.now() - renderStartTime.current;
        console.log(`[Perf] ManagerView rendered in ${duration.toFixed(2)}ms. Data: ${visibleUsers.length} users, ${visibleTasks.length} total visible tasks.`);
        // Reset for next potential render measure
        renderStartTime.current = performance.now();
    });

    // Helper: Sort tasks specifically for horizontal scanning
    // Order: Deep Work -> In Progress -> Blocked -> Todo -> Done
    const sortTasksForSwimlane = (taskList: typeof tasks) => {
        return [...taskList].sort((a, b) => {
            const score = (status: string) => {
                if (status === 'deep-work') return 0;
                if (status === 'in-progress') return 1;
                if (status === 'blocked') return 2;
                if (status === 'todo') return 3;
                return 4; // Done
            };
            const sA = score(a.status);
            const sB = score(b.status);
            if (sA !== sB) return sA - sB;
            return b.updatedAt - a.updatedAt; // Recent first
        });
    };

    const getTimeForUser = (timezone: string) => {
        try {
            return new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                timeZone: timezone,
            }).format(new Date());
        } catch (e) {
            return '--:--';
        }
    };

    if (managerTeamIds.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 text-center p-8">
                <p>You are not a Manager in any of the currently selected teams.</p>
                <p className="text-sm">Switch to the "My Focus" view or select a team where you are a Manager.</p>
            </div>
        )
    }

    const unassignedTasks = tasksByUser['unassigned'] || [];

    // Filter Unassigned for Search and remove completed tasks
    const filteredUnassigned = unassignedTasks.filter(t =>
        (searchQuery === '' || t.title.toLowerCase().includes(searchQuery.toLowerCase())) &&
        t.status !== 'done'
    );

    return (
        <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900" style={{ scrollbarGutter: 'stable' }}>
            <div className="sticky top-0 z-30 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 shadow-sm">
                <div className="px-6 py-4 flex flex-col gap-4">
                    <SelectedTeamsBar />

                    {/* Search Bar */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-xl leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all shadow-sm"
                            placeholder="Search users or tasks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="p-6 pb-6 flex flex-col gap-8">
                {/* 1. Unassigned / Backlog Row */}
                {filteredUnassigned.length > 0 && (
                    <div className="bg-white dark:bg-gray-800/40 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-300">
                                <Layers size={20} />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-gray-700 dark:text-gray-200">Unassigned / Backlog</h2>
                                <p className="text-xs text-gray-500">Tasks waiting to be picked up</p>
                            </div>
                            <span className="ml-auto bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold px-2.5 py-1 rounded-full">
                                {filteredUnassigned.length}
                            </span>
                        </div>
                        <HorizontalScroller>
                            {filteredUnassigned.map(task => (
                                <div key={task.id} className="min-w-[280px] max-w-[280px] shrink-0">
                                    <TaskCard task={task} fixedHeight />
                                </div>
                            ))}
                        </HorizontalScroller>
                    </div>
                )}

                {/* 2. User Swimlanes */}
                <div className="flex flex-col gap-6">
                    {sortedUsers.slice(0, visibleCount).map(user => {
                        if (!user || !user.id) return null;

                        // Filter tasks based on Search Query
                        const userTasksRaw = tasksByUser[user.id] || [];
                        const query = searchQuery.toLowerCase();
                        const displayName = user.displayName || 'Unknown User';
                        const isUserMatch = displayName.toLowerCase().includes(query);

                        // Show task if: The user matches (show all their tasks) OR the task itself matches
                        const filteredTasks = userTasksRaw.filter(t =>
                            searchQuery === '' || isUserMatch || t.title.toLowerCase().includes(query)
                        );

                        // If neither user nor any tasks match, hide the row
                        if (!isUserMatch && filteredTasks.length === 0 && searchQuery !== '') {
                            return null;
                        }

                        const userTasks = sortTasksForSwimlane(filteredTasks);
                        const relevantTeam = user.teams?.find(t => managerTeamIds.includes(t.teamId));
                        const roleDisplay = relevantTeam ? relevantTeam.role : 'Member';

                        // Stats
                        const activeCount = userTasks.filter(t => t.status === 'in-progress' || t.status === 'deep-work').length;
                        const blockedCount = userTasks.filter(t => t.status === 'blocked').length;

                        return (
                            <div 
                                key={user.id} 
                                className="flex flex-col md:flex-row gap-0 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden min-h-[180px]"
                            >

                                {/* User Header (Left Sidebar) */}
                                <div 
                                    onClick={() => openMemberDetail(user.id, sortedUsers.map(u => u.id))}
                                    className="w-full md:w-64 bg-gray-50 dark:bg-gray-800/80 md:border-r border-gray-200 dark:border-gray-700 p-5 shrink-0 flex flex-row md:flex-col items-center md:items-start gap-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors group/header"
                                >
                                    <div className="relative">
                                        <img
                                            src={user.photoURL || `https://ui-avatars.com/api/?name=${user.displayName}`}
                                            className="w-14 h-14 rounded-full object-cover ring-4 ring-white dark:ring-gray-700"
                                            alt=""
                                        />
                                        {activeCount > 0 && (
                                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 border-2 border-white dark:border-gray-800 rounded-full flex items-center justify-center">
                                                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                            </div>
                                        )}
                                        {blockedCount > 0 && (
                                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 border-2 border-white dark:border-gray-800 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                                                !
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0 text-left md:w-full">
                                        <h3 className="font-bold text-gray-900 dark:text-white truncate text-lg">{user.displayName}</h3>

                                        <div className="flex flex-col gap-1 mt-1">
                                            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                                <Clock size={12} />
                                                <span>{getTimeForUser(user.timezone || 'UTC')}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                                                    {roleDisplay}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Task Stream (Right Content) */}
                                <div className="flex-1 p-4 min-h-[140px] bg-white dark:bg-gray-800 flex items-center min-w-0">
                                    {userTasks.length === 0 ? (
                                        <div className="w-full h-full border-2 border-dashed border-gray-100 dark:border-gray-700 rounded-lg flex items-center justify-center text-gray-300 dark:text-gray-600 gap-2 min-h-[100px]">
                                            <User size={20} />
                                            <span className="text-sm font-medium">
                                                {searchQuery ? 'No matching tasks' : 'No active tasks'}
                                            </span>
                                        </div>
                                    ) : (
                                        <HorizontalScroller>
                                            {userTasks.map(task => (
                                                <div key={task.id} className="min-w-[280px] max-w-[280px] shrink-0">
                                                    <TaskCard task={task} fixedHeight />
                                                </div>
                                            ))}
                                        </HorizontalScroller>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ManagerView;
